import { CurrencyValue, FieldValue, HookedRecordResult, type Row } from '@ingestro/importer-react'

import type { Metadata } from 'modules/universal_importer/components/NuvoImporter/types'
import i18n from 'modules/core/lib/i18n'

// Type alias to avoid linter issues with any
export type CompatibleColumns = unknown[]

// Type guards and constructors to avoid type assertions
function isHookedRecordResult(value: unknown): value is HookedRecordResult {
  return typeof value === 'object' && value !== null && 'value' in value && 'info' in value
}

function isFieldValue(value: unknown): value is FieldValue {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null ||
    value === undefined
  )
}

function isCurrencyValue(value: unknown): value is CurrencyValue {
  return typeof value === 'object' && value !== null && 'value' in value && 'type' in value
}

// Helper to extract field value from row
export function getFieldValue(
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  key: string
): FieldValue {
  if (isHookedRecordResult(row[key]) || isCurrencyValue(row[key])) {
    return row[key]?.value || ''
  } else if (isFieldValue(row[key])) {
    return row[key]
  }
  return null
}

// Helper to create HookedRecordResult for errors
export function createHookedRecordResult(
  fieldValue: FieldValue,
  message: string
): HookedRecordResult {
  return {
    value: fieldValue,
    info: [{ message, level: 'error' }],
  }
}

// Helper to create RowResult from validation/transformation results
export function createRowResult(
  value: FieldValue | CurrencyValue | HookedRecordResult
): HookedRecordResult | FieldValue {
  if (isHookedRecordResult(value)) {
    return value // Return HookedRecordResult for errors
  } else if (isFieldValue(value)) {
    return value // Return FieldValue for success
  } else {
    return value.value // Extract value from CurrencyValue
  }
}

// Type for customization - minimal interface to avoid circular dependencies
type ColumnCustomizationInput = {
  key: string
  hidden?: boolean
  customizations?: Array<{
    type: string
    computeWith: string
    parameters?: Array<{ key: string; value: string }> | null
  }>
}

// Transformation function type matching transformations.ts
type TransformationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null
) => { value: string }

// Validation function type matching validations.ts
type ValidationFunction = (
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  metadata: Metadata,
  columns: CompatibleColumns,
  currentColumnKey: string,
  parameters?: { key: string; value: string }[] | null,
  changeLog?: Row | null
) => { value: FieldValue | CurrencyValue | HookedRecordResult }

// Helper to apply transformations and validations to a row
export function applyCustomizationsToRow(
  row: Record<string, FieldValue | CurrencyValue | HookedRecordResult>,
  customizations: ColumnCustomizationInput[],
  transformationFunctions: Record<string, TransformationFunction>,
  validationFunctions: Record<string, ValidationFunction>,
  metadata: Metadata,
  columns: CompatibleColumns,
  options?: {
    wrapWithRowResult?: boolean
    changeLog?: Row | null
  }
): Record<string, FieldValue | CurrencyValue | HookedRecordResult> {
  const newRow: Record<string, FieldValue | CurrencyValue | HookedRecordResult> = { ...row }

  customizations.forEach((column: ColumnCustomizationInput) => {
    if (column.customizations) {
      column.customizations.forEach((customization) => {
        // Apply transformation function
        const transformationFunction = transformationFunctions[customization.computeWith]
        if (transformationFunction && Object.prototype.hasOwnProperty.call(newRow, column.key)) {
          const result = transformationFunction(
            newRow,
            metadata,
            columns,
            column.key,
            customization.parameters
          )
          // Set the computed value
          newRow[column.key] = options?.wrapWithRowResult
            ? createRowResult(result.value)
            : result.value
        }

        // Apply validation function
        const validationFunction = validationFunctions[customization.computeWith]
        if (validationFunction && Object.prototype.hasOwnProperty.call(newRow, column.key)) {
          const result = validationFunction(
            newRow,
            metadata,
            columns,
            column.key,
            customization.parameters,
            options?.changeLog
          )
          // Set the computed error
          newRow[column.key] = options?.wrapWithRowResult
            ? createRowResult(result.value)
            : result.value
        }
      })
    }
  })

  return newRow
}

// Type guard to check if object has key property
export function hasKey(obj: unknown): obj is { key: string; [key: string]: unknown } {
  return typeof obj === 'object' && obj !== null && 'key' in obj
}

// Helper to find column by key
export function findColumnByKey(columns: CompatibleColumns, targetKey: string) {
  if (Array.isArray(columns)) {
    return columns.find((col) => hasKey(col) && col.key === targetKey) || null
  }
  return null
}

/**
 * Generates mappingValidation logic string for a column based on its validations
 * Following Nuvo's mappingValidation format where the logic represents the VALID case
 * @param columnKey - The key of the column to generate validation for
 * @param validations - Array of validation objects for this column
 * @param allColumns - All columns to check for cross-column dependencies
 * @returns Object with logic and errorMessage, or undefined if no mapping validation needed
 */
export function generateMappingValidation(
  columnKey: string,
  validations?: Array<{ validate: string; columns?: string[] }>,
  allColumns?: CompatibleColumns
): { logic: string; errorMessage: string } | undefined {
  if (!validations || validations.length === 0) {
    return undefined
  }

  const getColumnLabel = (key: string) => {
    const col = allColumns ? findColumnByKey(allColumns, key) : null
    if (col && typeof col === 'object' && 'label' in col && typeof col.label === 'string') {
      return col.label
    }
    return key
  }

  const hasRequired = validations.some((v) => v.validate === 'required')
  const requiredWith = validations.find((v) => v.validate === 'required_with')
  const requiredWithAll = validations.find((v) => v.validate === 'required_with_all')

  // If column has 'required' validation, it must always be mapped
  if (hasRequired) {
    const columnLabel = getColumnLabel(columnKey)
    return {
      logic: `mapped('${columnKey}')`,
      errorMessage: i18n.t('universal_importer.mapping_validation.required', { columnLabel }),
    }
  }

  // Handle required_with: this column is required if ANY of the specified columns are mapped
  // Logic: column must be mapped OR none of the dependent columns are mapped
  if (requiredWith && requiredWith.columns && requiredWith.columns.length > 0) {
    const dependentColumns = requiredWith.columns
    const andConditions = dependentColumns.map((col) => `!mapped('${col}')`).join(' && ')
    const columnLabel = getColumnLabel(columnKey)
    const dependentColumnLabels = dependentColumns.map(getColumnLabel)
    const dependentColumnLabelsText = dependentColumnLabels.join(', ')
    return {
      logic: `mapped('${columnKey}') || (${andConditions})`,
      errorMessage: i18n.t('universal_importer.mapping_validation.required_with', {
        columnLabel,
        dependentColumnLabels: dependentColumnLabelsText,
      }),
    }
  }

  // Handle required_with_all: this column is required if ALL of the specified columns are mapped
  // Logic: column must be mapped OR at least one of the dependent columns is not mapped
  if (requiredWithAll && requiredWithAll.columns && requiredWithAll.columns.length > 0) {
    const dependentColumns = requiredWithAll.columns
    const orConditions = dependentColumns.map((col) => `!mapped('${col}')`).join(' || ')
    const columnLabel = getColumnLabel(columnKey)
    const dependentColumnLabels = dependentColumns.map(getColumnLabel)
    const dependentColumnLabelsText = dependentColumnLabels.join(', ')
    return {
      logic: `mapped('${columnKey}') || (${orConditions})`,
      errorMessage: i18n.t('universal_importer.mapping_validation.required_with_all', {
        columnLabel,
        dependentColumnLabels: dependentColumnLabelsText,
      }),
    }
  }

  return undefined
}

/**
 * Adds mappingValidation property to columns based on their validations
 * @param columns - Array of column definitions
 * @returns Columns array with mappingValidation added where needed
 */
export function addMappingValidations(columns: CompatibleColumns): CompatibleColumns {
  const hasValidations = (
    col: unknown
  ): col is {
    key: string
    validations?: Array<{ validate: string; columns?: string[] }>
    mappingValidation?: { logic: string; errorMessage: string }
  } => {
    return (
      typeof col === 'object' &&
      col !== null &&
      'key' in col &&
      typeof col.key === 'string' &&
      (!('validations' in col) || Array.isArray(col.validations))
    )
  }

  const typedColumns = Array.isArray(columns) ? columns.filter(hasValidations) : []

  return typedColumns.map((col) => {
    const mappingValidation = generateMappingValidation(col.key, col.validations, columns)

    // Only add mappingValidation if it doesn't already exist and we generated one
    if (mappingValidation && !col.mappingValidation) {
      return {
        ...col,
        mappingValidation,
      }
    }

    return col
  })
}
